import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  FitnessCenter,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const LoginPage: React.FC = () => {
  const { signIn, signUp, resetPassword, loading } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [resetEmail, setResetEmail] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
    // Clear form fields when switching tabs
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFullName('');
    setResetEmail('');
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    const { error } = await signIn(email, password);
    if (error) {
      setError(error.message);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email || !password || !confirmPassword || !fullName) {
      setError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    const { error } = await signUp(email, password, fullName);
    if (error) {
      setError(error.message);
    } else {
      setSuccess('Account created successfully! Please check your email to verify your account.');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }

    const { error } = await resetPassword(resetEmail);
    if (error) {
      setError(error.message);
    } else {
      setSuccess('Password reset email sent! Please check your inbox.');
    }
  };

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 4 }}>
      <Paper 
        elevation={8} 
        sx={{ 
          width: '100%', 
          p: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FitnessCenter sx={{ fontSize: 32, color: 'white' }} />
            </Box>
          </Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Workout Tool
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Your personal fitness companion
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
              },
            }}
          >
            <Tab label="Sign In" />
            <Tab label="Sign Up" />
            <Tab label="Reset Password" />
          </Tabs>
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Sign In Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box component="form" onSubmit={handleSignIn}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  left: '48px',
                  zIndex: 1,
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    left: '14px',
                    top: '0px',
                    transform: 'translate(14px, -9px) scale(0.75)',
                    backgroundColor: 'rgba(31, 41, 55, 0.9)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    zIndex: 2,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  position: 'relative',
                  overflow: 'visible',
                },
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ zIndex: 10 }}>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{
                        zIndex: 10,
                        position: 'relative',
                        mr: 0.5,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  left: '48px',
                  zIndex: 1,
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    left: '14px',
                    top: '0px',
                    transform: 'translate(14px, -9px) scale(0.75)',
                    backgroundColor: 'rgba(31, 41, 55, 0.9)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    zIndex: 2,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  position: 'relative',
                  overflow: 'visible',
                  '& .MuiInputAdornment-root': {
                    zIndex: 10,
                    position: 'relative',
                  },
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                fontWeight: 'bold',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </Box>
        </TabPanel>

        {/* Sign Up Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box component="form" onSubmit={handleSignUp}>
            <TextField
              fullWidth
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  left: '48px',
                  zIndex: 1,
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    left: '14px',
                    top: '0px',
                    transform: 'translate(14px, -9px) scale(0.75)',
                    backgroundColor: 'rgba(31, 41, 55, 0.9)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    zIndex: 2,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  position: 'relative',
                  overflow: 'visible',
                },
              }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  left: '48px',
                  zIndex: 1,
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    left: '14px',
                    top: '0px',
                    transform: 'translate(14px, -9px) scale(0.75)',
                    backgroundColor: 'rgba(31, 41, 55, 0.9)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    zIndex: 2,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  position: 'relative',
                  overflow: 'visible',
                },
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              helperText="Password must be at least 6 characters long"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ zIndex: 10 }}>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{
                        zIndex: 10,
                        position: 'relative',
                        mr: 0.5,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  left: '48px',
                  zIndex: 1,
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    left: '14px',
                    top: '0px',
                    transform: 'translate(14px, -9px) scale(0.75)',
                    backgroundColor: 'rgba(31, 41, 55, 0.9)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    zIndex: 2,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  position: 'relative',
                  overflow: 'visible',
                  '& .MuiInputAdornment-root': {
                    zIndex: 10,
                    position: 'relative',
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  left: '48px',
                  zIndex: 1,
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    left: '14px',
                    top: '0px',
                    transform: 'translate(14px, -9px) scale(0.75)',
                    backgroundColor: 'rgba(31, 41, 55, 0.9)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    zIndex: 2,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  position: 'relative',
                  overflow: 'visible',
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                fontWeight: 'bold',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
            </Button>
          </Box>
        </TabPanel>

        {/* Reset Password Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box component="form" onSubmit={handleResetPassword}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  left: '48px',
                  zIndex: 1,
                  '&.Mui-focused, &.MuiFormLabel-filled': {
                    left: '14px',
                    top: '0px',
                    transform: 'translate(14px, -9px) scale(0.75)',
                    backgroundColor: 'rgba(31, 41, 55, 0.9)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    zIndex: 2,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  position: 'relative',
                  overflow: 'visible',
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                fontWeight: 'bold',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Email'}
            </Button>
          </Box>
        </TabPanel>

        {/* Footer */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="text.secondary" align="center">
          Secure authentication powered by Supabase
        </Typography>
      </Paper>
    </Container>
  );
}; 